import { create } from 'zustand'
import { SelectOption } from '../components/CustomSelect'
import { PhotoItem } from '../types/photo'
import { VisitType } from '../constants/visitType'
import { RankedZoneItem } from '../api/map'
import { getRecentPivots, rememberPivot } from '../utils/recentPivots'

const MAX_ZONE_COMPARISONS = 5

/** 첫 비교 상대를 중앙에서 몇 칸까지 벗어나 고를 수 있는지 (initZoneSearch 참고) */
const FIRST_PIVOT_WINDOW = 2

/**
 * 되돌리기(뒤로가기)로 복원할 한 판의 탐색 상태.
 *
 * 이진탐색 상태는 이 여섯 개가 전부다. 판을 넘기기 직전에 통째로 쌓아두면
 * 되돌릴 때 그대로 되돌려 놓기만 하면 된다 — 역연산을 계산하지 않으므로
 * 되돌린 뒤의 상태가 원래와 어긋날 여지가 없다.
 */
interface ZoneSearchSnapshot {
  searchLo: number
  searchHi: number
  currentPivot: number
  comparisonCount: number
  insertIndex: number | null
  skippedPivots: number[]
}

/**
 * 건너뛰기: 지금 구간 [lo, hi)에서 아직 안 보여준 다른 상대를 고른다. 없으면 null.
 *
 * 구간을 좁히지 않는다 — 사용자가 "모르겠다"고 한 것이지 순위를 답한 게 아니다.
 * 그래서 다음 판에도 답이 필요한 정보량은 그대로다.
 *
 * 중앙에서 가장 가까운 것부터 고른다. pivot이 [lo, hi) 안이기만 하면 최종
 * insertIndex는 어떤 pivot을 써도 정확하지만, 중앙에서 멀수록 남은 구간이
 * 덜 줄어든다. 비교 횟수 상한이 5판이라 그 손해가 그대로 오차가 된다.
 */
export function pickSkipPivot(
  lo: number,
  hi: number,
  current: number,
  skipped: number[],
): number | null {
  const mid = (lo + hi) >> 1
  let best: number | null = null
  for (let i = lo; i < hi; i++) {
    if (i === current || skipped.includes(i)) continue
    if (best === null || Math.abs(i - mid) < Math.abs(best - mid)) best = i
  }
  return best
}

/**
 * 이 장소에서 무엇을 하는 중인지 — 고치는 중인가, 새로 남기는 중인가.
 *
 * 왜 화면 상태가 아니라 스토어에 두나
 * ---------------------------------
 * 사진 추가·방문일자·키워드는 별도 화면으로 나갔다 온다. 그때 기록 화면이
 * 언마운트되면서 "새로 남기는 중"이라는 사실이 사라지고, 돌아오면 다시
 * '최근 기록 수정'으로 잡혀 **원래 기록을 덮어썼다**. 실제로 났던 일이다.
 *
 * placeId를 함께 들고 있어야 다른 가게로 옮겼을 때 남의 의도를 물려받지 않는다.
 */
export interface VisitIntent {
  placeId: string
  mode: 'edit' | 'new'
  /** 고치는 중이면 그 기록 id, 새로 남기는 중이면 null */
  reviewId: number | null
  /** 제목 위에 보여줄 회차 번호 */
  visitNumber: number | null
}

/**
 * A/B 비교 한 판의 기록.
 *
 * 왜 남기나
 * --------
 * 점수는 순위(insertIndex)로 정해지므로 저장에는 이게 없어도 된다. 다만
 * "누구와 견줘서 어떻게 골랐는지"가 없으면 비교가 잘 돌고 있는지 알 수 없다.
 * 첫 상대가 한쪽으로 쏠린다거나, 몇 판 만에 끝나는지 같은 걸 나중에 보려면
 * 남겨야 한다. (2026-06-08 이후 이 기록이 끊겨 있었다)
 *
 * low/high는 점수 구간이다. 탐색은 순위로 하지만, 기록은 예전 방식과 같은
 * 뜻이 되도록 그 구간의 양 끝 점수로 환산해 둔다.
 */
export interface ComparisonRound {
  comparePlaceId: number
  comparePlaceScore: number | null
  isCurrentBetter: boolean
  comparisonLow: number
  comparisonHigh: number
  comparisonCount: number
}

interface ReviewData {
  storeId: string | null
  /** 이번 기록에서 치른 A/B 비교들 (순서대로) */
  comparisonLog: ComparisonRound[]
  visitIntent: VisitIntent | null
  /** 리뷰 작성 화면 카테고리 드롭다운 옵션(API + 추가하기) */
  reviewCategoryOptions: SelectOption[]
  selectedCategory: SelectOption | null
  experienceScore: number
  photos: PhotoItem[]
  reviewText: string
  visitDate: string | null
  /**
   * visitDate 가 어디서 온 값인지.
   *
   *  none  아직 아무도 안 정함 → 방문일자 화면이 '지금'을 기본값으로 보여준다
   *  photo 사진 촬영일로 자동으로 채운 값 → 다음 사진이 와도 덮지 않는다(첫 사진이 이김)
   *  user  사용자가 직접 고른 값 → 무슨 일이 있어도 덮지 않는다
   *
   * 셋을 구분하지 않으면 "사진이 채운 값"과 "사용자가 고른 값"이 똑같이
   * 보여서, 사진을 하나 더 넣는 순간 사용자가 고친 날짜를 되돌려 버린다.
   */
  visitDateSource: 'none' | 'photo' | 'user'
  waitingTime: SelectOption | null // 웨이팅 시간 (없음, 15분~2시간, 2시간 이상)
  visitType: VisitType
  is_public: boolean
  minPricePerPerson: number | null // 최소 1인당 가격대
  maxPricePerPerson: number | null // 최대 1인당 가격대
  compareStoreIds: string[]
  // K=3 존+순위 비교 상태 (이진탐색)
  comparisonCount: number
  currentZone: number | null
  rankedList: RankedZoneItem[]
  searchLo: number
  searchHi: number
  /** 현재 유저에게 보여준 비교 상대의 인덱스(다음 라운드 narrowing 기준). 첫 상대는 중앙±윈도우 랜덤이라 (lo+hi)>>1로 재계산하면 안 됨 */
  currentPivot: number
  /**
   * 이번 판에서 건너뛴 상대들의 인덱스. 같은 상대를 다시 뽑지 않기 위한 것이라
   * 판이 넘어가면(applyZoneComparison) 비운다 — 구간이 좁아졌으므로 다시
   * 후보가 될 수 있는 자리는 애초에 남아 있지 않다.
   */
  skippedPivots: number[]
  /** 되돌리기용 이력. 판을 넘기거나 건너뛰기 직전 상태를 쌓는다. */
  zoneHistory: ZoneSearchSnapshot[]
  insertIndex: number | null
  // hashtags: string[] // 추후 추가
  taggedFriendIds: string[] // 친구 태그 목록
  /**
   * 태그한 친구의 표시 이름. id와 짝을 이룬다.
   *
   * 기록 작성 화면에서 '누구를 태그했는지'를 요약해 보여주려면 이름이 필요한데,
   * id만 들고 있으면 이름을 알 수 없다. 고른 순간에 함께 담아둔다 —
   * 그러면 이름을 다시 물어보러 서버에 갈 일이 없다.
   */
  taggedFriendNames: string[]
  reviewKeywordIds: number[] // 리뷰 키워드 ID 목록
  /** 고른 키워드의 이름. 위와 같은 이유로 함께 담는다 */
  reviewKeywordNames: string[]
  isReturningFromSubPage: boolean // 하위 페이지에서 돌아왔는지 여부
  storeInfo: {
    id: string // 가게 ID
    name: string
    category: string
    categoryCode: string
    address: string | null
    lat?: number | null // 위치 기반 사진 추천용
    lng?: number | null
    /** 세부 종류 라벨 ('일식' · '한식' …). A/B 비교 상대를 고를 때 쓴다. */
    subCategory?: string | null
  } | null // 가게 정보
}

interface ReviewStore extends ReviewData {
  setStoreId: (storeId: string | null) => void
  setVisitIntent: (intent: VisitIntent | null) => void
  setReviewCategoryOptions: (options: SelectOption[]) => void
  setSelectedCategory: (category: SelectOption | null) => void
  setExperienceScore: (score: number) => void
  setPhotos: (photos: PhotoItem[]) => void
  setReviewText: (text: string) => void
  setVisitDate: (date: string | null) => void
  /** 사진 촬영일로 방문일자를 채운다. 이미 정해진 값이 있으면 무시한다. */
  setVisitDateFromPhoto: (date: string) => void
  setWaitingTime: (waitingTime: SelectOption | null) => void
  setVisitType: (type: VisitType) => void
  setIsPublic: (isPublic: boolean) => void
  setPricePerPerson: (min: number | null, max: number | null) => void
  setCompareStoreIds: (ids: string[]) => void
  // K=3 존+순위: 비교 초기화 및 한 라운드 적용
  /** @param newKind 지금 기록 중인 가게의 종류. 첫 상대를 고를 때 같은 종류를 우선한다. */
  initZoneSearch: (
    zone: number,
    rankedList: RankedZoneItem[],
    newKind?: string | null,
    /** 최근 첫 상대를 기억할 때 쓰는 카테고리. 없으면 기억하지 않는다 */
    recentKey?: number | string | null,
  ) => RankedZoneItem | null
  applyZoneComparison: (isCurrentBetter: boolean) => {
    done: boolean
    insertIndex: number | null
    nextItem: RankedZoneItem | null
  }
  /** 건너뛰기: 같은 구간에서 다른 상대를 뽑는다. 뽑을 상대가 없으면 null(그대로 둔다). */
  skipZoneComparison: () => RankedZoneItem | null
  /** 뒤로가기: 직전 한 판(선택이든 건너뛰기든)을 되돌린다. 되돌릴 게 없으면 null. */
  undoZoneComparison: () => RankedZoneItem | null
  setTaggedFriendIds: (ids: string[], names?: string[]) => void
  /** 이름을 함께 넘기면 요약 표시에 쓴다 */
  toggleTaggedFriend: (userId: string, name?: string) => void
  setReviewKeywordIds: (ids: number[], names?: string[]) => void
  setIsReturningFromSubPage: (isReturning: boolean) => void
  setStoreInfo: (storeInfo: ReviewStore['storeInfo']) => void
  resetReviewData: () => void
  /** 지금 쓰고 있는 내용을 떠둔다 (회차를 바꿀 때 잃지 않게) */
  captureVisitDraft: () => VisitDraft
  /** 떠둔 내용을 되돌려 놓는다. 빈 객체를 주면 새 회차로 비운다 */
  applyVisitDraft: (draft: Partial<VisitDraft>) => void
  /** AB/비교 플로우만 종료 (뒤로가기 후 compareStoreIds 잔여 시 하단 UI 사라짐 방지) */
  exitComparisonMode: () => void
}

const initialReviewData: ReviewData = {
  storeId: null,
  comparisonLog: [],
  visitIntent: null,
  reviewCategoryOptions: [],
  selectedCategory: null,
  experienceScore: -1, // -1 = 미선택 (버튼 선택 전). 선택 시 존 대표 점수로 설정
  photos: [],
  reviewText: '',
  visitDate: null,
  visitDateSource: 'none' as const,
  waitingTime: null,
  visitType: VisitType.STORE,
  is_public: true,
  minPricePerPerson: null,
  maxPricePerPerson: null,
  compareStoreIds: [],
  comparisonCount: 0,
  currentZone: null,
  rankedList: [],
  searchLo: 0,
  searchHi: 0,
  currentPivot: 0,
  skippedPivots: [],
  zoneHistory: [],
  insertIndex: null,
  taggedFriendIds: [],
  taggedFriendNames: [],
  reviewKeywordNames: [],
  reviewKeywordIds: [],
  isReturningFromSubPage: false,
  storeInfo: null,
}

/**
 * 회차를 바꿔도 남아야 하는 것들 — 사용자가 직접 쓴 내용.
 *
 * 비교 진행 상태(searchLo/Hi, currentPivot, rankedList…)는 일부러 뺐다.
 * 회차마다 A/B 비교를 새로 하기 때문에, 옮겨오면 다른 회차의 탐색 구간이
 * 섞여 엉뚱한 순위가 나온다.
 *
 * storeId·storeInfo도 뺐다. 회차가 바뀌어도 가게는 그대로다.
 */
const VISIT_DRAFT_FIELDS = [
  'selectedCategory',
  'experienceScore',
  'photos',
  'reviewText',
  'visitDate',
  'visitDateSource',
  'waitingTime',
  'visitType',
  'is_public',
  'minPricePerPerson',
  'maxPricePerPerson',
  'taggedFriendIds',
  'taggedFriendNames',
  'reviewKeywordIds',
  'reviewKeywordNames',
] as const

export type VisitDraft = Pick<ReviewData, (typeof VISIT_DRAFT_FIELDS)[number]>

/** 빈 회차의 폼 상태 — 새 기록을 시작할 때 쓴다 */
const initialVisitDraft = (): VisitDraft => {
  const draft = {} as VisitDraft
  for (const key of VISIT_DRAFT_FIELDS) {
    ;(draft as Record<string, unknown>)[key] = initialReviewData[key]
  }
  return draft
}

const snapshotZoneSearch = (s: ReviewData): ZoneSearchSnapshot => ({
  searchLo: s.searchLo,
  searchHi: s.searchHi,
  currentPivot: s.currentPivot,
  comparisonCount: s.comparisonCount,
  insertIndex: s.insertIndex,
  skippedPivots: s.skippedPivots,
})

export const useReviewStore = create<ReviewStore>((set, get) => ({
  ...initialReviewData,
  setStoreId: (storeId) => set({ storeId }),
  setReviewCategoryOptions: (reviewCategoryOptions) =>
    set({ reviewCategoryOptions }),
  setSelectedCategory: (selectedCategory) => set({ selectedCategory }),
  setExperienceScore: (experienceScore) => set({ experienceScore }),
  setPhotos: (photos) => set({ photos }),
  setReviewText: (reviewText) => set({ reviewText }),
  setVisitDate: (visitDate) => set({ visitDate, visitDateSource: 'user' }),

  /**
   * 사진 촬영일로 방문일자를 채운다. 이미 정해진 값이 있으면 아무것도 하지 않는다.
   * (사용자가 고친 값은 물론, 먼저 넣은 사진이 채운 값도 지키기 위해)
   */
  setVisitDateFromPhoto: (visitDate) =>
    set((state) =>
      state.visitDateSource === 'none'
        ? { visitDate, visitDateSource: 'photo' }
        : {},
    ),
  setWaitingTime: (waitingTime) => set({ waitingTime }),
  setVisitType: (visitType) => set({ visitType }),
  setIsPublic: (is_public) => set({ is_public }),
  setPricePerPerson: (min, max) =>
    set({ minPricePerPerson: min, maxPricePerPerson: max }),
  setCompareStoreIds: (compareStoreIds) => set({ compareStoreIds }),
  // K=3 존+순위: 비교 초기화. 첫 비교 대상(없으면 null) 반환.
  initZoneSearch: (zone, rankedList, newKind, recentKey = null) => {
    const hi = rankedList.length
    // 첫 비교 상대: 항상 정중앙만 보여주면 같은 존이면 매번 같은 가게라 지루함.
    // 중앙(mid)을 기준으로 ±윈도우(존 크기의 1/4, 최소 1) 안에서 랜덤 선택.
    // pivot이 [0, hi) 안이기만 하면 이진탐색 정확도(최종 insertIndex)는 그대로 유지되고,
    // 중앙 근처라 비교 횟수도 거의 log를 유지 → 정확도 손실 없이 다양성만 확보.
    const mid = hi >> 1
    // 중앙에서 몇 칸까지 벗어나도 되는지. 넓히면 비슷한 걸 더 잘 찾지만
    // 마지막 등수가 부정확해진다. pickFirst 주석 참고.
    const w = FIRST_PIVOT_WINDOW
    const lo = Math.max(0, mid - w)
    const hiBound = Math.min(hi - 1, mid + w)

    /**
     * 첫 비교 상대 고르기.
     *
     * 두 가지를 같이 노린다.
     *  - 판단하기 쉽게: 일식은 일식과, 회는 회와 견주는 편이 낫다.
     *  - 정확하게: 이진탐색은 중앙에서 멀어질수록 마지막 등수가 부정확해진다.
     *
     * 그래서 창을 **중앙 ±2칸**으로 좁게 잡고 그 안에서만 같은 종류를 찾는다.
     * 예전에는 ±1/4(99곳이면 ±24칸)에서 무작위로 골랐는데, 그 넓은 창에서
     * 같은 종류를 찾게 하면 중앙에서 크게 벗어나 오차가 늘어난다
     * (실제 랭킹 그룹 15개로 재보니 한 그룹이 0.269 → 0.286으로 나빠졌다).
     *
     * ±2칸이면 어느 그룹도 지금보다 나빠지지 않는다. 오히려 창이 좁아진 덕에
     * 전체 평균 오차가 0.174 → 0.142로 줄고, 같은 종류끼리 비교한 판은
     * 1.24 → 1.47판(5판 중)으로 는다.
     *
     * 최근에 나온 곳은 잠시 뺀다
     * ------------------------
     * 창이 ±2칸(후보 5곳)이라 '좋아' 존처럼 큰 목록에서도 늘 같은 가게가 나왔다.
     * 게다가 같은 종류를 '항상' 고르면 사실상 한 곳으로 굳는다.
     *
     * 그래서 두 가지를 바꾼다.
     *  - 최근 첫 상대로 쓰인 곳(recentPivots)을 후보에서 뺀다.
     *  - 같은 종류는 우선하되 고정하지 않는다. 여럿이면 그중 무작위로 고른다.
     *
     * 창 크기는 그대로라 이진탐색 정확도는 손대지 않는다. 뺄 곳이 너무 많아
     * 후보가 비면 원래 후보로 되돌린다 — 상대가 없어 비교가 멈추면 안 된다.
     */
    const pickFirst = (): number => {
      if (hi === 0) return 0

      const all: number[] = []
      for (let i = lo; i <= hiBound; i++) all.push(i)

      const recent = recentKey !== null ? getRecentPivots(recentKey, zone) : []
      const fresh = all.filter((i) => !recent.includes(rankedList[i]?.placeId))
      const pool = fresh.length > 0 ? fresh : all

      // 같은 종류를 우선하되, 여럿이면 무작위 — 늘 같은 집이 나오지 않게
      const sameKind = newKind
        ? pool.filter((i) => rankedList[i]?.kind === newKind)
        : []
      const target = sameKind.length > 0 ? sameKind : pool

      return target[Math.floor(Math.random() * target.length)]
    }
    const pivot = pickFirst()
    if (recentKey !== null && rankedList[pivot]) {
      rememberPivot(recentKey, zone, rankedList[pivot].placeId)
    }
    set({
      currentZone: zone,
      rankedList,
      searchLo: 0,
      searchHi: hi,
      currentPivot: pivot,
      skippedPivots: [],
      zoneHistory: [],
      insertIndex: hi === 0 ? 0 : null,
      comparisonCount: 0,
      // 비교를 새로 시작하면 지난 판 기록도 버린다
      comparisonLog: [],
    })
    if (hi === 0) return null
    return rankedList[pivot] ?? null
  },
  // 한 비교 라운드 적용. 신규가 더 좋으면 위쪽(작은 인덱스)으로 좁힘.
  applyZoneComparison: (isCurrentBetter) => {
    let res: {
      done: boolean
      insertIndex: number | null
      nextItem: RankedZoneItem | null
    } = { done: true, insertIndex: 0, nextItem: null }

    set((state) => {
      // 방금 보여준 상대는 state.currentPivot (첫 라운드는 랜덤 pivot이라 (lo+hi)>>1 재계산 금지)
      const pivot = state.currentPivot
      const history = [...state.zoneHistory, snapshotZoneSearch(state)]
      let lo = state.searchLo
      let hi = state.searchHi

      /*
        이 판을 기록해 둔다 — 좁히기 **전**의 구간이 이 판에서 답해야 했던 범위다.
        구간의 양 끝 점수로 적는다. 목록은 점수 내림차순이라
        lo가 위(높은 점수), hi-1이 아래(낮은 점수)다.
      */
      const opponent = state.rankedList[pivot] ?? null
      const top = state.rankedList[state.searchLo]?.score
      const bottom = state.rankedList[state.searchHi - 1]?.score
      const round: ComparisonRound = {
        comparePlaceId: Number(opponent?.placeId ?? 0),
        comparePlaceScore: opponent?.score ?? null,
        isCurrentBetter,
        comparisonLow: bottom ?? opponent?.score ?? 0,
        comparisonHigh: top ?? opponent?.score ?? 0,
        comparisonCount: state.comparisonCount + 1,
      }
      const log = opponent ? [...state.comparisonLog, round] : state.comparisonLog
      if (isCurrentBetter) {
        hi = pivot // 신규가 더 좋음 → 상위 절반 [lo, pivot)
      } else {
        lo = pivot + 1 // 신규가 더 아래 → [pivot+1, hi)
      }
      const count = state.comparisonCount + 1
      const reachedCap = count >= MAX_ZONE_COMPARISONS
      const converged = lo >= hi
      if (converged || reachedCap) {
        res = { done: true, insertIndex: lo, nextItem: null }
        return {
          searchLo: lo,
          searchHi: hi,
          comparisonCount: count,
          insertIndex: lo,
          skippedPivots: [],
          zoneHistory: history,
          comparisonLog: log,
        }
      }
      // 2번째 이후는 기존대로 중앙값(비교 횟수 최소화)
      const nextMid = (lo + hi) >> 1
      res = {
        done: false,
        insertIndex: null,
        nextItem: state.rankedList[nextMid] ?? null,
      }
      return {
        searchLo: lo,
        searchHi: hi,
        currentPivot: nextMid,
        comparisonCount: count,
        insertIndex: null,
        skippedPivots: [],
        zoneHistory: history,
        comparisonLog: log,
      }
    })

    return res
  },

  /**
   * 건너뛰기 — 답하지 않고 다른 상대를 본다.
   *
   * 구간도, 비교 횟수도 건드리지 않는다. 상한 5판은 "답한 판"만 센다.
   * 건너뛴 것까지 세면 다섯 번 건너뛴 사람은 아무 답도 안 한 채 구간이
   * 처음 그대로인 상태로 확정되어(insertIndex = lo = 0) 그 존의 1등이 된다.
   *
   * 뽑을 상대가 없으면(구간에 남은 자리를 다 봤으면) 아무것도 하지 않는다.
   */
  skipZoneComparison: () => {
    let next: RankedZoneItem | null = null
    set((state) => {
      const idx = pickSkipPivot(
        state.searchLo,
        state.searchHi,
        state.currentPivot,
        state.skippedPivots,
      )
      if (idx === null) return {}
      const item = state.rankedList[idx]
      if (!item) return {}
      next = item
      return {
        zoneHistory: [...state.zoneHistory, snapshotZoneSearch(state)],
        skippedPivots: [...state.skippedPivots, state.currentPivot],
        currentPivot: idx,
      }
    })
    return next
  },

  /**
   * 뒤로가기 — 직전 한 판을 되돌린다.
   *
   * 쌓아둔 상태를 통째로 되돌려 놓고, 그때 보고 있던 상대를 반환한다.
   * 선택이든 건너뛰기든 똑같이 한 칸이다(화면에서 마지막으로 한 행동이 곧
   * 되돌릴 대상이라 사용자가 예상하는 것과 어긋나지 않는다).
   */
  undoZoneComparison: () => {
    let prev: RankedZoneItem | null = null
    set((state) => {
      const last = state.zoneHistory[state.zoneHistory.length - 1]
      if (!last) return {}
      const item = state.rankedList[last.currentPivot]
      if (!item) return {}
      prev = item
      return {
        zoneHistory: state.zoneHistory.slice(0, -1),
        searchLo: last.searchLo,
        searchHi: last.searchHi,
        currentPivot: last.currentPivot,
        comparisonCount: last.comparisonCount,
        insertIndex: last.insertIndex,
        skippedPivots: last.skippedPivots,
        /*
          되돌린 판의 기록도 지운다. 안 지우면 "취소한 선택"이 기록에 남아,
          나중에 분석할 때 실제로 고르지 않은 답이 섞인다.
          건너뛰기는 애초에 기록하지 않으므로(판단이 아니다) 여기서도 안전하다.
        */
        comparisonLog: state.comparisonLog.slice(0, -1),
      }
    })
    return prev
  },
  setTaggedFriendIds: (taggedFriendIds, taggedFriendNames) =>
    set(
      taggedFriendNames
        ? { taggedFriendIds, taggedFriendNames }
        : { taggedFriendIds },
    ),
  toggleTaggedFriend: (userId: string, name?: string) => {
    set((state) => {
      const isSelected = state.taggedFriendIds.includes(userId)
      // 이름도 id와 같은 순서로 넣고 뺀다. 하나가 빠지면 짝이 어긋난다.
      const at = state.taggedFriendIds.indexOf(userId)
      const names = isSelected
        ? state.taggedFriendNames.filter((_, i) => i !== at)
        : [...state.taggedFriendNames, name ?? '']
      return {
        taggedFriendNames: names,
        taggedFriendIds: isSelected
          ? state.taggedFriendIds.filter((id) => id !== userId)
          : [...state.taggedFriendIds, userId],
      }
    })
  },
  setReviewKeywordIds: (reviewKeywordIds, reviewKeywordNames) =>
    set(
      reviewKeywordNames
        ? { reviewKeywordIds, reviewKeywordNames }
        : { reviewKeywordIds },
    ),
  setIsReturningFromSubPage: (isReturningFromSubPage) =>
    set({ isReturningFromSubPage }),
  setStoreInfo: (storeInfo) => set({ storeInfo }),
  setVisitIntent: (visitIntent) => set({ visitIntent }),
  resetReviewData: () => set(initialReviewData),
  captureVisitDraft: () => {
    const s = get()
    const draft = {} as VisitDraft
    for (const key of VISIT_DRAFT_FIELDS) {
      ;(draft as Record<string, unknown>)[key] = s[key]
    }
    return draft
  },
  applyVisitDraft: (draft) =>
    set({
      // 사용자가 쓴 내용만 갈아끼우고, 비교 진행 상태는 건드리지 않는다.
      // 회차마다 A/B 비교는 새로 하기 때문이다.
      ...initialVisitDraft(),
      ...draft,
    }),
  exitComparisonMode: () =>
    set({
      compareStoreIds: [],
      comparisonCount: 0,
      currentZone: null,
      rankedList: [],
      searchLo: 0,
      searchHi: 0,
      skippedPivots: [],
      zoneHistory: [],
      insertIndex: null,
    }),
}))
